import { useCallback, useMemo } from "react";
import { useCurrentUser } from "@/hooks/use-current-user";
import { usePermissions } from "@/hooks/use-permissions";
import { useProject } from "@/hooks/use-project";
import {
  canCloseIssue,
  canPostIssueUpdate,
  issueStatusAction,
  type IssueAccessFields,
  type IssueStatusAction,
} from "@/lib/issues";

/** What this viewer may do to one issue's status from a list row. */
export interface IssueStatusAccess {
  /** The sheet's form: an ordinary update, or a reopen. */
  action: IssueStatusAction;
  /** Whether the sheet may offer `closed`. */
  canClose: boolean;
  /** Whether the sheet may offer `in_progress` and `completed`. */
  canUpdate: boolean;
}

export type IssueStatusAccessFor = (
  issue: IssueAccessFields,
) => IssueStatusAccess | null;

/**
 * Per-issue status permissions for a project's issues list — the same rule the
 * issue page's Update/Reopen bar applies, resolved row by row. `null` means the
 * row offers nothing. See [[issue-item.spec]] § Behavior.
 *
 * While the project loads, nothing is offered: an action that appears and then
 * vanishes is worse than one that appears a moment late.
 */
export function useIssueStatusAccess(projectId: string): IssueStatusAccessFor {
  const { data: project } = useProject(projectId);
  const { data: currentUser } = useCurrentUser();
  // Memoised so `can` — and the resolver below — keep their identity between
  // renders, rather than changing with a fresh object every time.
  const permissionsProject = useMemo(
    () =>
      project
        ? { owner: project.owner.id, my_roles: project.my_roles }
        : { owner: "", my_roles: [] },
    [project],
  );
  const { can } = usePermissions(permissionsProject);
  const currentUserId = currentUser?.id;

  return useCallback(
    (issue) => {
      const action = issueStatusAction(issue, currentUserId, can);
      if (!action) return null;
      return {
        action,
        canClose: canCloseIssue(issue, currentUserId, can),
        canUpdate: canPostIssueUpdate(issue, currentUserId, can),
      };
    },
    [can, currentUserId],
  );
}
